import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  Spinner,
  FormFeedback,
} from "reactstrap";
import Select from "react-select";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { candidateMarkAsRejected } from "model/CandidateModel";
import { commonReasonData, commonReasonsForAll, submitFilterData as submitReasonFilterData } from "model/ReasonModel";
import { mapReasonMasterFilterRowsToSelectOptions } from "helpers/callDetailsCallNotesFieldRules";

const reactSelectMenuPortalStyles = {
  menuPortal: (baseStyles) => ({ ...baseStyles, zIndex: 9999 }),
  menu: (baseStyles) => ({ ...baseStyles, zIndex: 9999 }),
};

const markAsRejectedSchema = Yup.object().shape({
  notes: Yup.string().trim().required("Notes is required"),
});

const CandidateMarkAsRejectedModal = ({
  isOpen,
  onClose,
  candidateId,
  onSuccess,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [reasonOptions, setReasonOptions] = useState([]);
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const [selectedReason, setSelectedReason] = useState(null);
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) return;
    setSelectedReason(null);
    setNotes("");
    setErrors({});
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    let requestCancelled = false;
    (async () => {
      setReasonsLoading(true);
      try {
        const filterResponse = await commonReasonsForAll({});
        if (requestCancelled) return;
        const reasonRows = Array.isArray(filterResponse?.data)
          ? filterResponse.data
          : [];
        setReasonOptions(mapReasonMasterFilterRowsToSelectOptions(reasonRows));
      } catch {
        if (!requestCancelled) setReasonOptions([]);
      } finally {
        if (!requestCancelled) setReasonsLoading(false);
      }
    })();
    return () => {
      requestCancelled = true;
    };
  }, [isOpen]);

  const handleReasonChange = (option) => {
    setSelectedReason(option);
  };

  const handleNotesChange = async (ev) => {
    const nextNotes = ev.target.value;
    setNotes(nextNotes);
    if (!errors.notes) return;
    try {
      await Yup.reach(markAsRejectedSchema, "notes").validate(nextNotes);
      setErrors((prev) => ({ ...prev, notes: "" }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, notes: err.message }));
    }
  };

  const handleNotesBlur = async () => {
    try {
      await Yup.reach(markAsRejectedSchema, "notes").validate(notes);
      setErrors((prev) => ({ ...prev, notes: "" }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, notes: err.message }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const id = String(candidateId || "").trim();
    if (!id) {
      toast.error("Candidate ID not found");
      return;
    }

    const trimmedNotes = String(notes ?? "").trim();
    const reasonId = String(selectedReason?.value ?? "").trim();

    try {
      await markAsRejectedSchema.validate({ notes: trimmedNotes }, { abortEarly: false });
      setErrors({});
    } catch (validationError) {
      const validationErrors = {};
      validationError?.inner?.forEach((errorItem) => {
        if (errorItem?.path && !validationErrors[errorItem.path]) {
          validationErrors[errorItem.path] = errorItem.message;
        }
      });
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    try {
      const response = await candidateMarkAsRejected(id, {
        note: trimmedNotes,
        reasonId: reasonId || "",
      });
      if (response?.success || response?.isSuccess) {
        toast.success(response?.message || "Candidate marked as rejected");
        const responseData =
          response?.data && typeof response.data === "object" ? response.data : {};
        onSuccess?.({
          id,
          candidateId: id,
          profileStatus: responseData?.profileStatus ?? "not interested",
          reasonId: responseData?.reasonId ?? reasonId,
          reason: responseData?.reason ?? selectedReason?.label ?? "",
          note: responseData?.note ?? trimmedNotes,
          ...responseData,
        });
        onClose?.();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    if (!submitting) onClose?.();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={closeModal}
      backdrop="static"
      size="md"
      className="modal-full-height"
      centered
    >
      <Form onSubmit={handleSubmit}>
        <ModalBody className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="fw-bold mb-0">Mark as rejected</h5>
            <Button close type="button" onClick={closeModal} disabled={submitting} />
          </div>
          <hr />

          <FormGroup>
            <Label>Reason</Label>
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              options={reasonOptions}
              value={selectedReason}
              onChange={handleReasonChange}
              isLoading={reasonsLoading}
              isClearable
              placeholder="Select reason"
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              styles={reactSelectMenuPortalStyles}
              menuPosition="fixed"
            />
          </FormGroup>

          <FormGroup>
            <Label>Notes <span className="text-danger">*</span></Label>
            <Input
              type="textarea"
              rows={4}
              value={notes}
              onChange={handleNotesChange}
              onBlur={handleNotesBlur}
              placeholder="Enter notes"
              invalid={!!errors.notes}
            />
            <FormFeedback>{errors.notes}</FormFeedback>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" type="button" onClick={closeModal} disabled={submitting}>
            Cancel
          </Button>
          <Button color="primary" type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner size="sm" className="me-1" /> Submitting…
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CandidateMarkAsRejectedModal;
